import { createSlice, PayloadAction, } from '@reduxjs/toolkit';
import {
  fetchTypeProducts,
  fetchFilteredProducts,
  fetchGenderProducts,
  fetchMaterialIconsByProductIds,
  fetchImagesByMaterialForHomePage,
  persistProductSelection,
} from '../../services';
import { IProductTypes, ProductsTypeState,  } from "../../components/interfaces";

const initialState: ProductsTypeState = {
  products: [],
  unqTypes: [] as IProductTypes[],
  status: 'idle',
  error: null,
  currentType: null,
  skip: 0,
  hasMore: true,
  materialImages: {},
  productImagesByProductId: {},
  materialIconsByProductId: {},
  statusByProductId: {},
  errorByProductId: {},
};

export const productsTypeSlice = createSlice({
  name: 'ProductsType',
  initialState,
  reducers: {
    updateSelectedMaterialInType: (
      state,
      action: PayloadAction<{ productId: string; materialName: string }>
    ) => {
      const { productId, materialName } = action.payload;
      const product = state.products?.find((p) => p.id === productId);
      if (product) {
        product.selectedMaterial = materialName;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTypeProducts.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(fetchTypeProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.unqTypes = action.payload;
      })
      .addCase(fetchTypeProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.toString() ?? 'Unknown error';
      })
      .addCase(fetchFilteredProducts.pending, (state, action) => {
        state.status = 'loading';
        state.error = '';

        if (action.meta.arg.skip === 0 && state.currentType !== action.meta.arg.type) {
          state.products = [];
          state.skip = 0;
          state.hasMore = true;
        }
      })
      .addCase(fetchFilteredProducts.fulfilled, (state, action) => {
        const { type, products, take, skip } = action.payload;
        const isFreshTypeLoad = state.currentType !== type || skip === 0;
        const nextProducts = isFreshTypeLoad
          ? products
          : [
              ...state.products,
              ...products.filter(
                (product) => !state.products.some((existing) => existing.id === product.id)
              ),
            ];

        state.status = 'succeeded';
        state.currentType = type;
        state.products = nextProducts;
        state.skip = skip + products.length;
        state.hasMore = products.length >= take;
      })
      .addCase(fetchFilteredProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.toString() ?? 'Unknown error';
      })
      .addCase(fetchGenderProducts.pending, (state, action) => {
        state.status = 'loading';
        state.error = '';

        const nextCollection = `gender:${action.meta.arg.gender}`;
        if (action.meta.arg.skip === 0 && state.currentType !== nextCollection) {
          state.products = [];
          state.skip = 0;
          state.hasMore = true;
        }
      })
      .addCase(fetchGenderProducts.fulfilled, (state, action) => {
        const { gender, products, take, skip } = action.payload;
        const collection = `gender:${gender}`;
        const isFreshLoad = state.currentType !== collection || skip === 0;
        const nextProducts = isFreshLoad
          ? products
          : [
              ...state.products,
              ...products.filter(
                (product) => !state.products.some((existing) => existing.id === product.id)
              ),
            ];

        state.status = 'succeeded';
        state.currentType = collection;
        state.products = nextProducts;
        state.skip = skip + products.length;
        state.hasMore = products.length >= take;
      })
      .addCase(fetchGenderProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.toString() ?? 'Unknown error';
      })
      .addCase(fetchImagesByMaterialForHomePage.pending, (state, action) => {
        const { productId, source } = action.meta.arg;
        if (source === 'types') {
          state.statusByProductId[productId] = 'loading';
          state.errorByProductId[productId] = '';
        }
      })
      .addCase(fetchImagesByMaterialForHomePage.fulfilled, (state, action) => {
        const { productId, images, source } = action.payload;
        if (source === 'types') {
          state.productImagesByProductId[productId] = images.map((img) => img.image.publicUrl);
          state.statusByProductId[productId] = 'succeeded';
        }
      })
      .addCase(fetchImagesByMaterialForHomePage.rejected, (state, action) => {
        const { productId, source } = action.meta.arg;
        if (source === 'types') {
          state.statusByProductId[productId] = 'failed';
          state.errorByProductId[productId] = action.payload || 'Unknown error';
        }
      })
      .addCase(fetchMaterialIconsByProductIds.fulfilled, (state, action) => {
        const source = (action.meta.arg as any).source;
        if (source === 'types') {
          state.materialIconsByProductId = {
            ...state.materialIconsByProductId,
            ...action.payload,
          };
        }
      })
      .addCase(persistProductSelection.fulfilled, (state, action) => {
        const product = state.products.find((item) => item.id === action.payload.productId);
        if (product && action.payload.selectedMaterial) {
          product.selectedMaterial = action.payload.selectedMaterial;
        }
      });
  },
});

export const selectCurrentproduct = (state: { productsType: ProductsTypeState }) => state.productsType.products;

export const { updateSelectedMaterialInType } = productsTypeSlice.actions;

export default productsTypeSlice.reducer;
